import clsx from "clsx";
import "./FilterDropDownV2.scss";
import { ArrowDown } from "@/icons/ArrowDown";
import { useEffect, useRef, useState } from "react";
import { ArrowList } from "@/icons/ArrowList";
import { Col } from "@/components";
import { useFocusTrap } from "@/hooks/useFocusTrap";

export type FilterDropDownV2Item = {
  label: string;
  value: string;
};

type FilterDropDownV2Props = {
  items: FilterDropDownV2Item[];
  className?: string;
  value?: string;
  label?: string;
  setValue: (filter: string) => void;
};
export const FilterDropDownV2 = ({
  items = [],
  className,
  label,
  value,
  setValue,
}: FilterDropDownV2Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useFocusTrap(listRef, isExpanded);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsExpanded(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isExpanded]);

  return (
    <div ref={dropdownRef} className={clsx(className, "ui-filter-dropdown-v2")}>
      <button
        className="ui-button-accordion-v2"
        type="button"
        aria-expanded={isExpanded}
        aria-haspopup="listbox"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h1 className="fw-semibold fs-custom text-primary">
          {value || label}
        </h1>
        <ArrowDown
          color="primary"
          className={clsx("ui-arrow-v2", { "ui-expanded-v2": isExpanded })}
        />
      </button>
      {isExpanded && (
        <div
          ref={listRef}
          className={"ui-wrap-list-item-filter-v2 position-relative pt-x12"}
        >
          <ArrowList className={"ui-arrow-list-v2 position-absolute"} />
          <div className={"p-x24 h-full shadow"}>
            <Col
              gap={"x24"}
              text={"primary"}
              fs={"custom"}
              fw={"regular"}
              className={"ui-list-item-filter-v2 h-full overflow fw-regular"}
              role="listbox"
            >
              {items.map((item, index) => {
                return (
                  <div
                    key={index}
                    role="option"
                    aria-selected={item.value === value}
                    tabIndex={0}
                    onClick={() => {
                      setValue(item.value);
                      setIsExpanded(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setValue(item.value);
                        setIsExpanded(false);
                      }
                    }}
                  >
                    {item.label}
                  </div>
                );
              })}
            </Col>
          </div>
        </div>
      )}
    </div>
  );
};
